import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { TMEstimateId } from '@business/entities/tm';
import { EstimatorTemplateListWithSelectionResponse } from '@business/entities/tm/EstimatorTemplateListResponse';
import { TMEstimateLineItemResponse } from '@business/entities/tm/TMEstimateResponse';
import { EstimateTemplateListAPI } from '@business/api/estimator_template_list';
import { handleMessage, requestMessage } from '@business/messages';
import { tap } from 'rxjs/operators';

export class EstimateListBloc extends BaseBloc {
  private readonly repo = new Repository<EstimatorTemplateListWithSelectionResponse>({
    getItemId: item => item.id,
  });

  readonly items$ = this.repo.items$;
  readonly selectItem = this.repo.selectItem;

  onReset() {
    this.repo.reset();
  }

  fetchListsForEstimate = (estimateId: TMEstimateId) =>
    EstimateTemplateListAPI.fetchEstimatorTemplateListsForEstimate(estimateId).pipe(
      this.repo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_estimate_lists_error') }),
    );

  createItem = (estimateId: TMEstimateId, listId: string, providerId?: string | null) =>
    EstimateTemplateListAPI.createEstimateListItem(estimateId, listId, providerId).pipe(
      tap(item => {
        const list = this.repo.getItem(listId as any);
        if (list) {
          this.repo.upsertOne({ item: { ...list, selected: true, estimateListItem: item } });
        }
      }),
      handleMessage({ type: requestMessage('create_estimate_list_item') }),
    );

  updateItem = (
    id: string,
    data: Partial<Pick<TMEstimateLineItemResponse, 'quantity' | 'manualQty' | 'laborPrice' | 'materialPrice' | 'weProvide'> & { providerId: string | null }>,
  ) =>
    EstimateTemplateListAPI.updateEstimateListItem(id, data).pipe(
      tap(item => {
        const list = this.repo.items.find(l => l.estimateListItem?.id === id);
        if (list) {
          this.repo.upsertOne({ item: { ...list, estimateListItem: item } });
        }
      }),
      handleMessage({ type: requestMessage('update_estimate_list_item') }),
    );

  deleteItem = (id: string) =>
    EstimateTemplateListAPI.deleteEstimateListItem(id).pipe(
      tap(() => {
        const list = this.repo.items.find(l => l.estimateListItem?.id === id);
        if (list) {
          this.repo.upsertOne({ item: { ...list, selected: false, estimateListItem: null } });
        }
      }),
      handleMessage({ type: requestMessage('delete_estimate_list_item') }),
    );
}
